import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateIt, formatDateTimeIt } from "@/lib/format";
import { ChevronLeft, ChevronRight, Users, PlayCircle, CheckSquare, Truck,
    PhoneCall, Receipt, Briefcase, FileSignature, RefreshCw, X, Info } from "lucide-react";

function workWeekBounds(ref) {
    const d = new Date(ref); d.setHours(0, 0, 0, 0);
    const day = d.getDay();          // JS: Sun=0, Thu=4
    const daysBack = (day - 4 + 7) % 7;
    const start = new Date(d); start.setDate(d.getDate() - daysBack);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    return { start, end };
}
const fmtISO = (d) => d.toISOString();
const toDateInput = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
};
// ISO date (yyyy-mm-dd) of the given day's Thursday-start work-week
const weekKeyOf = (d) => toDateInput(workWeekBounds(d).start);

// All counters shown in the grid, in display order
const METRICS = [
    { key: "clients_created", label: "Nuovi nomi nel CF", icon: Users, tone: "neutral" },
    { key: "projects_started", label: "Progetti avviati", icon: PlayCircle, tone: "blue" },
    { key: "projects_delivered", label: "Progetti consegnati", icon: Truck, tone: "teal" },
    { key: "tasks_completed", label: "Fasi di progetto completate", icon: CheckSquare, tone: "green" },
    { key: "tasks_revisione", label: "Revisioni", icon: RefreshCw, tone: "purple", noQuota: true },
    { key: "notes_followup", label: "Follow-up", icon: PhoneCall, tone: "sky" },
    { key: "notes_preventivo", label: "Preventivi inviati", icon: Receipt, tone: "amber" },
    { key: "notes_consulenza", label: "Consulenze introduttive consegnate", icon: Briefcase, tone: "violet" },
    { key: "notes_contratto", label: "Contratti inviati", icon: FileSignature, tone: "emerald" },
];

export default function Production() {
    const initial = workWeekBounds(new Date());
    const [start, setStart] = useState(initial.start);
    const [end, setEnd] = useState(initial.end);
    const [mode, setMode] = useState("week");
    const [data, setData] = useState(null);
    const [prevData, setPrevData] = useState(null);
    const [weekQuotas, setWeekQuotas] = useState({}); // { "yyyy-mm-dd": { metric: n } }
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null); // { metric, label }

    const currentKey = weekKeyOf(start);
    const currentQuotas = weekQuotas[currentKey] || {};

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Load current week + previous week (for the "auto quota = prev+1" default)
            const prevStart = new Date(start); prevStart.setDate(prevStart.getDate() - 7);
            const prevEnd = new Date(end); prevEnd.setDate(prevEnd.getDate() - 7);
            const [s, sp, p] = await Promise.all([
                api.get("/production/summary", { params: { start: fmtISO(start), end: fmtISO(end) } }),
                api.get("/production/summary", { params: { start: fmtISO(prevStart), end: fmtISO(prevEnd) } }),
                api.get("/settings/preferences"),
            ]);
            setData(s.data);
            setPrevData(sp.data);
            setWeekQuotas(p.data?.report_quotas_by_week || {});
        } finally { setLoading(false); }
    }, [start, end]);
    useEffect(() => { load(); }, [load]);

    const setQuota = async (metric, rawValue) => {
        // Interpret empty as "remove"; otherwise parse int
        const trimmed = (rawValue ?? "").toString().trim();
        const n = trimmed === "" ? null : Number(trimmed);
        if (trimmed !== "" && (isNaN(n) || n < 0)) return;
        const nextForWeek = { ...currentQuotas };
        if (n == null) delete nextForWeek[metric];
        else nextForWeek[metric] = n;
        const nextAll = { ...weekQuotas };
        if (Object.keys(nextForWeek).length === 0) delete nextAll[currentKey];
        else nextAll[currentKey] = nextForWeek;
        setWeekQuotas(nextAll);
        try {
            await api.patch("/settings/preferences", { report_quotas_by_week: nextAll });
        } catch {
            // reload on error to resync
            load();
        }
    };

    const shiftWeek = (delta) => {
        const s = new Date(start); s.setDate(s.getDate() + delta * 7);
        const b = workWeekBounds(s); setStart(b.start); setEnd(b.end); setMode("week");
    };

    return (
        <div className="p-6" data-testid="production-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Report</h1>
                    <div className="text-sm text-neutral-500">Conteggio produzione per periodo · <span className="text-neutral-400">i progetti interni sono esclusi</span></div>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-wrap items-center gap-2 mb-4" data-testid="period-controls">
                <button onClick={() => shiftWeek(-1)} data-testid="prev-week-btn"
                    className="text-xs border border-neutral-200 rounded px-2 py-1 inline-flex items-center gap-1 hover:bg-neutral-50">
                    <ChevronLeft size={14} /> Settimana precedente
                </button>
                <button onClick={() => { const b = workWeekBounds(new Date()); setStart(b.start); setEnd(b.end); setMode("week"); }}
                    data-testid="this-week-btn"
                    className={`text-xs rounded px-2 py-1 border ${mode === "week" ? "bg-neutral-900 text-white border-[#FFE500]" : "border-neutral-200 hover:bg-neutral-50"}`}>
                    Settimana corrente
                </button>
                <button onClick={() => shiftWeek(1)} data-testid="next-week-btn"
                    className="text-xs border border-neutral-200 rounded px-2 py-1 inline-flex items-center gap-1 hover:bg-neutral-50">
                    Settimana successiva <ChevronRight size={14} />
                </button>
                <div className="mx-2 h-4 w-px bg-neutral-200" />
                <label className="text-xs text-neutral-600 inline-flex items-center gap-1">
                    Dal <input type="date" data-testid="custom-start" value={toDateInput(start)}
                        onChange={(e) => { if (e.target.value) { const d = new Date(e.target.value + "T00:00:00"); setStart(d); setMode("custom"); } }}
                        className="text-xs border border-neutral-200 rounded px-1.5 py-0.5" />
                </label>
                <label className="text-xs text-neutral-600 inline-flex items-center gap-1">
                    Al <input type="date" data-testid="custom-end" value={toDateInput(end)}
                        onChange={(e) => { if (e.target.value) { const d = new Date(e.target.value + "T23:59:59"); setEnd(d); setMode("custom"); } }}
                        className="text-xs border border-neutral-200 rounded px-1.5 py-0.5" />
                </label>
                <div className="ml-auto text-xs text-neutral-500" data-testid="period-label">
                    Periodo: <b className="text-neutral-800">{formatDateIt(start.toISOString())} → {formatDateIt(end.toISOString())}</b>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {METRICS.map((m) => (
                    <Card key={m.key} metric={m} value={data?.[m.key]}
                        quota={currentQuotas[m.key]}
                        quotaDefault={(prevData?.[m.key] ?? 0) + 1}
                        onQuotaChange={(v) => setQuota(m.key, v)}
                        loading={loading}
                        onDetails={() => setDetails({ metric: m.key, label: m.label })} />
                ))}
            </div>

            <div className="mt-6 text-xs text-neutral-500">
                Settimana lavorativa: <b>giovedì → mercoledì</b>. I conteggi si basano sulla data di inserimento del cliente, sulla prima transizione del progetto verso "In corso" o "Consegnato", sulla data di completamento delle task e sulla data di creazione delle note e delle task di tipo "Revisione".
            </div>

            {details && (
                <DetailsModal metric={details.metric} label={details.label}
                    start={fmtISO(start)} end={fmtISO(end)}
                    onClose={() => setDetails(null)} />
            )}
        </div>
    );
}

function Card({ metric, value, quota, quotaDefault, onQuotaChange, loading, onDetails }) {
    const Icon = metric.icon;
    const noQuota = !!metric.noQuota;
    const [draft, setDraft] = useState(quota == null ? "" : String(quota));
    useEffect(() => { setDraft(quota == null ? "" : String(quota)); }, [quota]);
    const effective = quota != null && quota !== ""
        ? Number(quota)
        : (quotaDefault != null ? Number(quotaDefault) : null);
    const validEffective = effective != null && !isNaN(effective);
    const met = !noQuota && validEffective && (value ?? 0) >= effective;
    const commit = () => {
        const cur = quota == null ? "" : String(quota);
        if (draft === cur) return;
        onQuotaChange(draft);
    };
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-4 flex flex-col" data-testid={`stat-${metric.key}`}>
            <div className="flex items-center justify-between text-neutral-500">
                <span className="text-xs uppercase tracking-wider font-medium leading-tight">{metric.label}</span>
                <Icon size={16} />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
                <div className={`font-display text-3xl font-semibold tracking-tight ${met ? "text-emerald-600" : "text-neutral-900"}`} data-testid={`stat-value-${metric.key}`}>
                    {loading ? "…" : (value ?? 0)}
                </div>
                {!noQuota && (
                    <>
                        <span className="text-neutral-300 text-lg">/</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
                            onBlur={commit}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                            placeholder={quotaDefault != null ? String(quotaDefault) : "—"}
                            title={quota != null ? "Quota di questa settimana" : "Quota automatica (settimana scorsa +1). Digita per sovrascrivere."}
                            data-testid={`stat-quota-input-${metric.key}`}
                            className="w-14 text-lg bg-transparent text-neutral-300 hover:text-neutral-500 focus:text-neutral-700 focus:outline-none focus:border-b focus:border-neutral-400 placeholder-neutral-300 tracking-tight"
                        />
                    </>
                )}
            </div>
            <button onClick={onDetails} data-testid={`details-${metric.key}`}
                disabled={loading}
                className="mt-3 self-start text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 border border-neutral-200 rounded px-2 py-1 hover:bg-neutral-50">
                <Info size={12} /> Dettaglio
            </button>
        </div>
    );
}

function DetailsModal({ metric, label, start, end, onClose }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/production/details", { params: { metric, start, end } });
                setItems(data.items || []);
            } finally { setLoading(false); }
        })();
    }, [metric, start, end]);

    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col" data-testid="details-modal">
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <div className="text-xs text-neutral-500">Dettaglio · {items.length} element{items.length === 1 ? "o" : "i"}</div>
                        <div className="font-display font-semibold">{label}</div>
                    </div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="overflow-y-auto flex-1">
                    {loading && <div className="p-6 text-sm text-neutral-500">Caricamento…</div>}
                    {!loading && items.length === 0 && <div className="p-6 text-sm text-neutral-500">Nessun elemento in questo periodo.</div>}
                    {!loading && items.length > 0 && (
                        <ul className="divide-y divide-neutral-100">
                            {items.map((it, i) => <DetailRow key={`${it.type}-${it.id || i}`} it={it} />)}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function DetailRow({ it }) {
    if (it.type === "project") {
        return (
            <li className="px-5 py-3 hover:bg-neutral-50">
                <Link to={`/progetti/${it.id}`} className="block">
                    <div className="text-sm font-medium text-neutral-900 truncate">{it.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                        {it.project_type === "continuous" ? "Continuativo" : "Spot"}
                        {it.client_name ? ` · ${it.client_name}` : ""}
                        {it.company ? ` (${it.company})` : ""}
                    </div>
                </Link>
            </li>
        );
    }
    if (it.type === "client") {
        return (
            <li className="px-5 py-3 hover:bg-neutral-50">
                <Link to={`/clienti/${it.id}`} className="block">
                    <div className="text-sm font-medium text-neutral-900">{it.name || "—"}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                        {it.company ? `${it.company} · ` : ""}Inserito: {formatDateTimeIt(it.created_at)}
                    </div>
                </Link>
            </li>
        );
    }
    if (it.type === "task") {
        return (
            <li className="px-5 py-3 hover:bg-neutral-50">
                <Link to={`/progetti/${it.project_id}`} className="block">
                    <div className="text-sm font-medium text-neutral-900 truncate">{it.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                        {it.project_title}{it.client_name ? ` · ${it.client_name}` : ""} · {it.task_type} · {it.level === "tertiary" ? "Task secondaria" : "Task"}
                        {it.completed_at && ` · completata il ${formatDateIt(it.completed_at)}`}
                        {!it.completed_at && it.created_at && ` · creata il ${formatDateIt(it.created_at)}`}
                    </div>
                </Link>
            </li>
        );
    }
    if (it.type === "note") {
        return (
            <li className="px-5 py-3 hover:bg-neutral-50">
                <Link to={`/clienti/${it.client_id}`} className="block">
                    <div className="text-sm text-neutral-800 truncate">{it.snippet || "—"}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                        {it.client_name || "—"}{it.company ? ` (${it.company})` : ""} · {formatDateTimeIt(it.created_at)}
                    </div>
                </Link>
            </li>
        );
    }
    return null;
}
